import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import type { AuthContextType, User, SignUpRequest, SignUpResponse } from '../types/auth';
import { authService } from '../services/authService';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize auth state from localStorage on mount
  useEffect(() => {
    const initializeAuth = () => {
      try {
        const storedToken = authService.storage.getToken();
        const storedUser = authService.storage.getUser();

        if (storedToken && storedUser) {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
        }
      } catch (error) {
        console.error('Failed to initialize auth:', error);
        authService.storage.clear();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Listen for token refresh events from apiClient
  useEffect(() => {
    const handleTokenRefresh = (event: CustomEvent) => {
      const authResponse = event.detail;
      if (authResponse && authResponse.token && authResponse.user) {
        setToken(authResponse.token);
        setUser(authResponse.user);
      }
    };

    window.addEventListener('auth:token-refreshed', handleTokenRefresh as EventListener);

    return () => {
      window.removeEventListener('auth:token-refreshed', handleTokenRefresh as EventListener);
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      const response = await authService.signIn({ email, password });

      setUser(response.user);
      setToken(response.token);

      // Persist to localStorage
      authService.storage.setToken(response.token);
      authService.storage.setRefreshToken(response.refreshToken);
      authService.storage.setUser(JSON.stringify(response.user));
    } catch (error) {
      console.error('Sign in failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    try {
      setIsLoading(true);
      const response = await authService.signInWithGoogle();

      setUser(response.user);
      setToken(response.token);

      authService.storage.setToken(response.token);
      authService.storage.setRefreshToken(response.refreshToken);
      authService.storage.setUser(JSON.stringify(response.user));
    } catch (error) {
      console.error('Google sign in failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signInWithApple = async () => {
    try {
      setIsLoading(true);
      const response = await authService.signInWithApple();

      setUser(response.user);
      setToken(response.token);

      authService.storage.setToken(response.token);
      authService.storage.setRefreshToken(response.refreshToken);
      authService.storage.setUser(JSON.stringify(response.user));
    } catch (error) {
      console.error('Apple sign in failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = () => {
    setUser(null);
    setToken(null);
    authService.storage.clear();
  };

  const signUp = async (data: SignUpRequest): Promise<SignUpResponse> => {
    try {
      setIsLoading(true);
      const response = await authService.signUp(data);

      // If email verification is not required, set user and token immediately
      if (!response.requiresEmailVerification) {
        setUser(response.user);
        setToken(response.token);

        // Persist to localStorage
        authService.storage.setToken(response.token);
        authService.storage.setRefreshToken(response.refreshToken);
        authService.storage.setUser(JSON.stringify(response.user));
      }

      return response;
    } catch (error) {
      console.error('Sign up failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const refreshAuth = async () => {
    try {
      const refreshToken = authService.storage.getRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await authService.refreshToken(refreshToken);

      setUser(response.user);
      setToken(response.token);

      authService.storage.setToken(response.token);
      authService.storage.setRefreshToken(response.refreshToken);
      authService.storage.setUser(JSON.stringify(response.user));
    } catch (error) {
      console.error('Token refresh failed:', error);
      signOut();
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!user && !!token,
    isLoading,
    signIn,
    signInWithGoogle,
    signInWithApple,
    signUp,
    signOut,
    refreshAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
